import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, CalendarBlank } from "@phosphor-icons/react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import NewsletterBand from "@/components/NewsletterBand";

const CATS = ["all", "Specialized", "S-Works", "Trek", "Sur-Ron", "General"];
const TOPICS = ["all", "Guides", "Reviews", "Routes", "News"];
const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("all");
  const [topic, setTopic] = useState("all");

  useEffect(() => { api.get("/blog?limit=200").then((r) => setPosts(r.data)); }, []);

  const filtered = posts.filter((p) => (cat === "all" || p.category === cat) && (topic === "all" || p.topic === topic));
  const [featured, ...rest] = filtered;

  return (
    <>
      <SEO title="E-Bike Blog — Expert Guides, Reviews & UK Buying Advice" description="50+ expert guides on Specialized, Trek and Sur-Ron e-bikes: 2026 reviews, comparisons, UK law, maintenance and buying advice from Ebikes&Gear." path="/blog" />
      <PageHero title="The Ebikes&Gear Journal" subtitle="Expert reviews, comparisons and no-nonsense buying advice for the 2026 UK e-bike market — written by riders, for riders." image="https://images.unsplash.com/photo-1516892402839-eff85703da5b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" crumbs={[{ label: "Blog" }]} />

      <section className="container-px py-12">
        <div className="flex flex-wrap gap-2" data-testid="blog-category-filter">
          {CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)} className={`rounded-full border px-4 py-2 text-sm font-semibold capitalize transition-colors ${cat === c ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`} data-testid={`blog-cat-${c.toLowerCase()}`}>
              {c === "all" ? "All posts" : c}
            </button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="blog-topic-filter">
          {TOPICS.map((t) => (
            <button key={t} onClick={() => setTopic(t)} className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold capitalize transition-colors ${topic === t ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:border-foreground"}`} data-testid={`blog-topic-${t.toLowerCase()}`}>
              {t === "all" ? "All topics" : t}
            </button>
          ))}
        </div>

        {featured && (
          <Reveal>
            <Link to={`/blog/${featured.slug}`} className="group mt-8 grid overflow-hidden rounded-[2rem] border border-border bg-card lg:grid-cols-2" data-testid="blog-featured">
              <div className="aspect-[16/10] overflow-hidden lg:aspect-auto">
                <img src={featured.cover} alt={featured.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="flex flex-col justify-center p-8 lg:p-12">
                <span className="overline">{featured.category} · {featured.read_time}</span>
                <h2 className="mt-3 text-2xl sm:text-3xl font-black tracking-tight group-hover:text-primary transition-colors">{featured.title}</h2>
                <p className="mt-3 text-muted-foreground">{featured.excerpt}</p>
                <div className="mt-5 flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><CalendarBlank size={15} /> {fmtDate(featured.created_at)}</span>
                  <span className="inline-flex items-center gap-2 font-semibold text-primary">Read article <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></span>
                </div>
              </div>
            </Link>
          </Reveal>
        )}

        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.06}>
              <Link to={`/blog/${p.slug}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card transition-all hover:-translate-y-1 hover:shadow-soft" data-testid={`blog-card-${p.slug}`}>
                <div className="aspect-[16/10] overflow-hidden"><img src={p.cover} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                <div className="flex flex-1 flex-col p-6">
                  <span className="overline">{p.category}</span>
                  <h3 className="mt-2 text-lg font-bold leading-tight group-hover:text-primary transition-colors">{p.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{p.excerpt}</p>
                  <div className="mt-auto flex items-center gap-4 pt-4 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5"><CalendarBlank size={14} /> {fmtDate(p.created_at)}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {p.read_time}</span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14"><NewsletterBand /></Reveal>
      </section>
    </>
  );
}
